'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Field } from '@eliadmin/core/src/components/form/floating-label-field/field';
import Link from '@eliadmin/core/src/components/link';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    custom: z.object({
      workplace: z.string().optional(),
      institution: z.string().optional(),
    }),
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactPageForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      custom: {
        workplace: '',
        institution: '',
      },
      name: '',
      phone: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full max-w-[520px] flex-col items-start justify-start gap-5 bg-white p-5 sm:p-6 md:gap-6 lg:p-8',
          'ea-contact-page-form',
          'ea-contact-form',
        )}
      >
        <div
          className={cn(
            'grid w-full grid-cols-12 gap-5 md:gap-6',
            'ea-contact-page-form__fields-wrapper',
          )}
        >
          <Field
            required
            control={form.control}
            name="name"
            label={t('text2399')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <Field
            required
            control={form.control}
            name="email"
            type="email"
            label="Email"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            required
            control={form.control}
            name="phone"
            label={t('text36')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            control={form.control}
            name="custom.workplace"
            label={t('text2400')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <Field
            control={form.control}
            name="custom.institution"
            label={t('text2401')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <Field
            control={form.control}
            name="text"
            label={t('text80')}
            type="textarea"
            className={{ wrapper: 'ea-contact-form__textarea col-span-12 w-full' }}
          />
        </div>

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="col-span-12 flex w-full flex-col items-start justify-start gap-4 sm:flex-row sm:justify-between">
          <p className="text-sm text-primary">
            {t('text2341')}{' '}
            <Link
              prefetch={false}
              href={'/ochrana-osobnych-udajov'}
              className="font-medium text-secondary underline"
            >
              {t('text614')}
            </Link>{' '}
            {t('text2402')}.
          </p>
          <Button
            type="submit"
            variant={'secondary'}
            className={cn('w-full sm:w-fit sm:whitespace-nowrap', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
